import { useEffect } from "react";
import { supabase } from "@/integrations/db/client";
import { useQueryClient } from "@tanstack/react-query";

/**
 * Keeps assignment, due-date and reminder notifications fresh app-wide.
 *
 * Events carry only {table, op, id}, so this invalidates instead of patching:
 * the list query embeds backlog_item and assigned_by joins that a raw row
 * doesn't have (patching them in used to render "#undefined" entries), and
 * the refetch is already scoped to the signed-in user by its filter and RLS.
 */
export const useGlobalNotificationsSync = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const channel = supabase
      .channel("notifications-sync")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "assignment_notifications",
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["assignment-notifications"] });
          queryClient.invalidateQueries({ queryKey: ["unread-assignment-count"] });
        },
      )
      // Due soon, overdue and reminders (#9112): written by the dispatcher, so
      // this is the only way an open tab learns of them without a reload.
      .on("postgres_changes", { event: "*", schema: "public", table: "ticket_notifications" }, () => {
        queryClient.invalidateQueries({ queryKey: ["ticket-notifications"] });
      })
      // A reminder firing stamps fired_at, which drops it from the ticket's list.
      .on("postgres_changes", { event: "*", schema: "public", table: "ticket_reminders" }, () => {
        queryClient.invalidateQueries({ queryKey: ["ticket-reminders"] });
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);
};
